/**
 * Date utilities for availability calendar - client side
 * Handles Phoenix timezone operations for the frontend
 */

/**
 * Get today's date in Phoenix timezone as YYYY-MM-DD string
 * @returns {string} Date in YYYY-MM-DD format
 */
export const phoenixTodayISO = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Phoenix',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};

/**
 * Check if a date string is in the past relative to Phoenix timezone
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {boolean} True if the date is in the past
 */
export const isDateInPastPhoenix = (dateStr) => {
  const today = phoenixTodayISO();
  return dateStr < today;
};

/**
 * Convert a Date object to YYYY-MM-DD string in Phoenix timezone
 * @param {Date} date - Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export const dateToPhoenixISO = (date) => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Phoenix',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

/**
 * Get dates that should be disabled in a datepicker
 * Combines booked dates and past dates
 * @param {string[]} bookedDates - Array of YYYY-MM-DD strings
 * @returns {string[]} Array of YYYY-MM-DD strings to disable
 */
export const getDisabledDates = (bookedDates = []) => {
  const today = phoenixTodayISO();
  const disabledDates = [...bookedDates];
  
  // Add past dates (last 30 days to be safe)
  const pastDate = new Date();
  pastDate.setDate(pastDate.getDate() - 30);
  
  for (let i = 0; i < 30; i++) {
    const dateStr = dateToPhoenixISO(pastDate);
    if (dateStr < today) {
      disabledDates.push(dateStr);
    }
    pastDate.setDate(pastDate.getDate() + 1);
  }
  
  // Remove duplicates and sort
  return [...new Set(disabledDates)].sort();
};

/**
 * Fetch provider availability from the API
 * @param {string} providerId - The provider's ID
 * @param {string} [listingId] - Optional listing ID to help fetch availability plan
 * @param {object} [availabilityPlan] - Optional availability plan to pass as fallback
 * @returns {Promise<{disabledDates: string[]}>} Availability data
 */
export const fetchProviderAvailability = async (providerId, listingId, availabilityPlan) => {
  if (!providerId) {
    throw new Error('Provider ID is required');
  }

  try {
    let url = `/api/availability/${providerId}`;
    const queryParams = [];
    
    if (listingId) {
      queryParams.push(`listingId=${encodeURIComponent(listingId)}`);
    }
    
    // Only include plan if it actually has entries; otherwise it can block better inference on server
    const planHasEntries = availabilityPlan && Array.isArray(availabilityPlan.entries) && availabilityPlan.entries.length > 0;
    if (planHasEntries) {
      queryParams.push(`availabilityPlan=${encodeURIComponent(JSON.stringify(availabilityPlan))}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Handle different error types gracefully
      if (response.status === 403) {
        console.warn('Availability API is disabled - falling back to basic date validation');
        return {
          disabledDates: getDisabledDates([]), // Just past dates
          bookedDates: [],
          providerId,
          fallback: true
        };
      } else if (response.status === 500) {
        console.warn('Availability API server error - falling back to basic date validation');
        return {
          disabledDates: getDisabledDates([]), // Just past dates
          bookedDates: [],
          providerId,
          fallback: true
        };
      } else {
        throw new Error(`Failed to fetch availability: ${response.status} ${response.statusText}`);
      }
    }

    const data = await response.json();
    
    // The API now returns both bookedDates and disabledDates (which includes availability plan restrictions)
    // Use disabledDates if available (new format), otherwise fall back to bookedDates (legacy format)
    const bookedDates = data.bookedDates || [];
    const disabledDatesFromAPI = data.disabledDates || [];
    
    // If API provides disabledDates, use them directly (they include plan restrictions + bookings)
    // Otherwise, combine booked dates with past dates (legacy behavior)
    const disabledDates = disabledDatesFromAPI.length > 0 
      ? getDisabledDates(disabledDatesFromAPI) 
      : getDisabledDates(bookedDates);
    
    console.log(`📅 Availability data for ${providerId}:`, {
      bookedDates: bookedDates.length,
      disabledDates: disabledDates.length,
      hasAvailabilityPlan: !!data.availabilityPlan
    });
    
    // Return the expected format for the calendar component
    return {
      disabledDates,
      bookedDates,
      providerId: data.providerId,
      asOf: data.asOf,
      rangeStart: data.rangeStart,
      rangeEnd: data.rangeEnd,
      availabilityPlan: data.availabilityPlan
    };
  } catch (error) {
    console.error('Error fetching provider availability:', error);
    
    // Graceful fallback - return basic date validation (only past dates disabled)
    console.warn('Falling back to basic date validation due to API error');
    return {
      disabledDates: getDisabledDates([]), // Just past dates
      bookedDates: [],
      providerId,
      fallback: true
    };
  }
};
